import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
const OTP_LENGTH = 6;
const PassportOtpAuthenticationDialog = ({ open, onClose, onVerify, onResend, loading = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [otpDigits, setOtpDigits] = useState(() => Array(OTP_LENGTH).fill(''));
    const [timeLeft, setTimeLeft] = useState(0);
    const inputRefs = useRef([]);
    const timerRef = useRef(null);
    const startResendTimer = () => {
        setTimeLeft(60);
        if (timerRef.current)
            clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    if (timerRef.current)
                        clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };
    useEffect(() => {
        if (open) {
            setOtpDigits(Array(OTP_LENGTH).fill(''));
            startResendTimer();
        }
        else {
            setTimeLeft(0);
            if (timerRef.current)
                clearInterval(timerRef.current);
        }
    }, [open]);
    useEffect(() => {
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
        };
    }, []);
    const otpValue = useMemo(() => otpDigits.join(''), [otpDigits]);
    const handleChangeDigit = (index, value) => {
        const numericValue = value.replace(/\D/g, '').slice(-1);
        setOtpDigits((prev) => {
            const next = [...prev];
            next[index] = numericValue;
            return next;
        });
        if (numericValue && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handleKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };
    const handleClose = () => {
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        onClose();
    };
    const content = (_jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, p: 1, children: [_jsx(Typography, { variant: 'body2', children: t('events.eventDetails.passportTab.otpAuthenticationDescription') }), _jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, children: otpDigits.map((digit, index) => (_jsx(TextField, { inputRef: (el) => {
                        inputRefs.current[index] = el;
                    }, value: digit, onChange: (event) => handleChangeDigit(index, event.target.value), onFocus: (e) => e.target.select(), onKeyDown: (event) => handleKeyDown(index, event), inputProps: {
                        maxLength: 1,
                        style: {
                            textAlign: 'center',
                            fontSize: 18,
                            fontWeight: 600
                        }
                    }, sx: {
                        width: 56,
                        '& .MuiOutlinedInput-root': {
                            bgcolor: theme.palette.grey[100],
                            borderRadius: 2,
                            height: 56
                        },
                        '& .MuiOutlinedInput-input': {
                            textAlign: 'center',
                            padding: '0 !important',
                            height: '56px',
                            lineHeight: '56px',
                            boxSizing: 'border-box',
                            '&::selection': { backgroundColor: 'transparent' }
                        }
                    } }, index))) })] }));
    const actions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: () => {
                    setOtpDigits(Array(OTP_LENGTH).fill(''));
                    onResend?.();
                    startResendTimer();
                }, disabled: timeLeft > 0, fullWidth: true, children: timeLeft > 0
                    ? `${t('events.eventDetails.passportTab.resend')} (${timeLeft}s)`
                    : t('events.eventDetails.passportTab.resend') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', onClick: () => onVerify?.(otpValue), disabled: loading || otpValue.length < OTP_LENGTH, fullWidth: true, startIcon: loading ? _jsx(CircularProgress, { size: 16, color: 'inherit' }) : undefined, children: t('events.eventDetails.passportTab.verify') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { open: open, onClose: handleClose, title: t('events.eventDetails.passportTab.otpAuthenticationTitle'), anchor: 'bottom', showBottomBar: true, dialogActions: actions, slotProps: {
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(25px)',
                        WebkitBackdropFilter: 'blur(25px)'
                    }
                }
            }, children: _jsx(Box, { sx: { p: 2 }, children: content }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleClose, titleWithIcon: t('events.eventDetails.passportTab.otpAuthenticationTitle'), titleIcon: '/assets/images/otp-icon.png', maxWidth: 'md', backgroundVariant: 'white', backdropBlur: 25, closeIcon: true, actions: actions, children: _jsx(Box, { p: 1.5, children: content }) }));
};
export default PassportOtpAuthenticationDialog;
